import React, { useState, useEffect } from 'react';
import './MotivationalLoader.css';

interface MotivationalLoaderProps {
  type?: 'dashboard' | 'onboarding' | 'general';
  size?: 'sm' | 'md' | 'lg';
  showMessage?: boolean;
  className?: string;
}

const motivationalMessages = {
  dashboard: [
    "Analyzing customer success metrics...",
    "Calculating growth opportunities...",
    "Optimizing onboarding experiences...",
    "Tracking customer progress...",
    "Building success stories...",
    "Empowering customer journeys...",
    "Creating magical moments...",
    "Boosting team productivity...",
    "Measuring customer happiness...",
    "Driving business growth..."
  ],
  onboarding: [
    "Setting up your success journey...",
    "Preparing personalized experience...",
    "Loading your progress milestones...",
    "Crafting your onboarding path...",
    "Initializing growth opportunities...",
    "Building your success story...",
    "Customizing your experience...",
    "Preparing helpful resources...",
    "Loading your achievements...",
    "Creating your roadmap..."
  ],
  general: [
    "Making magic happen...",
    "Preparing something awesome...",
    "Getting things ready...",
    "Almost there...",
    "Loading excellence...",
    "Crafting your experience...",
    "Building success...",
    "Creating value...",
    "Optimizing performance...",
    "Delivering results..."
  ]
};

const MotivationalLoader: React.FC<MotivationalLoaderProps> = React.memo(({
  type = 'general',
  size = 'md',
  showMessage = true,
  className = ''
}) => {
  const [currentMessage, setCurrentMessage] = useState(0);
  const [progress, setProgress] = useState(0);
  const messages = motivationalMessages[type];

  useEffect(() => {
    const messageInterval = setInterval(() => {
      setCurrentMessage(prev => (prev + 1) % messages.length);
    }, 2500);

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) return 0;
        return prev + Math.random() * 15 + 5;
      });
    }, 200);

    return () => {
      clearInterval(messageInterval);
      clearInterval(progressInterval);
    };
  }, [messages.length]);

  const sizeClasses = {
    sm: 'loader-sm',
    md: 'loader-md',
    lg: 'loader-lg'
  };

  return (
    <div className={`motivational-loader ${sizeClasses[size]} ${className}`}>
      <div className="loader-container">
        {/* Main animated logo/icon */}
        <div className="loader-icon">
          <div className="loader-circle">
            <div className="loader-inner-circle">
              <svg className="loader-svg" viewBox="0 0 100 100">
                {/* Animated progress ring */}
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  className="progress-ring-bg"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  className="progress-ring"
                  style={{
                    strokeDasharray: `${Math.min(progress, 100) * 2.83} 283`,
                    transform: 'rotate(-90deg)',
                    transformOrigin: '50% 50%'
                  }}
                />
                
                {/* Center icon that pulses */}
                <g className="center-icon">
                  <circle cx="50" cy="50" r="25" className="icon-bg" />
                  {/* Mailercloud-style envelope icon */}
                  <path
                    d="M35 40 L50 50 L65 40 M35 40 L35 60 L65 60 L65 40"
                    className="envelope-icon"
                    fill="none"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {/* Success checkmark animation */}
                  <path
                    d="M42 50 L47 55 L58 44"
                    className="checkmark-icon"
                    fill="none"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
              </svg>
            </div>
          </div>
          
          {/* Floating particles */}
          <div className="loader-particles">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="particle"
                style={{
                  animationDelay: `${i * 0.3}s`,
                  animationDuration: `${2 + i * 0.2}s`
                }}
              />
            ))}
          </div>
        </div>

        {/* Progress bar */}
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
          <div className="progress-text">
            {Math.min(Math.round(progress), 100)}%
          </div>
        </div>

        {/* Motivational messages */}
        {showMessage && (
          <div className="message-container">
            <div className="message-text" key={currentMessage}>
              {messages[currentMessage]}
            </div>
            <div className="message-dots">
              <span className="dot" />
              <span className="dot" />
              <span className="dot" />
            </div>
          </div>
        )}

        {/* Success metrics animation */}
        <div className="success-metrics">
          <div className="metric-item">
            <div className="metric-icon">📈</div>
            <div className="metric-text">Growth</div>
          </div>
          <div className="metric-item">
            <div className="metric-icon">🎯</div>
            <div className="metric-text">Goals</div>
          </div>
          <div className="metric-item">
            <div className="metric-icon">⚡</div>
            <div className="metric-text">Speed</div>
          </div>
          <div className="metric-item">
            <div className="metric-icon">🏆</div>
            <div className="metric-text">Success</div>
          </div>
        </div>
      </div>
    </div>
  );
});

MotivationalLoader.displayName = 'MotivationalLoader';

export default MotivationalLoader;